import { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Button,
  Input,
  Text,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogTitle,
  DialogBackdrop,
  DialogPositioner,
  Portal,
} from '@chakra-ui/react';
import { toast } from 'react-toastify';
import * as authApi from '../api/auth';
import { useAuth } from '../hooks/useAuth';

const EmailVerificationModal = () => {
  const { user, refreshUser } = useAuth();
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleVerify = async () => {
    if (!user?.email || code.length !== 6) return;

    setIsLoading(true);
    setError(null);

    try {
      await authApi.verifyEmail({ email: user.email, code });
      toast.success('Email verified!');
      await refreshUser();
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Verification failed. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!user?.email) return;

    setIsResending(true);
    setError(null);

    try {
      await authApi.resendVerificationCode(user.email);
      toast.success('Verification code sent to your email');
      setResendCooldown(60);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to resend code. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <DialogRoot open={true} placement="center">
      <Portal>
        <DialogBackdrop bg="blackAlpha.600" />
        <DialogPositioner>
          <DialogContent
            backgroundColor="neutral.800"
            borderWidth="1px"
            borderColor="neutral.700"
            borderRadius="8px"
            maxW="440px"
            p={{ base: '20px', md: '24px' }}
          >
            <DialogHeader pb="0">
              <DialogTitle
                fontSize="26px"
                fontWeight="500"
                color="neutral.100"
              >
                Verify Your Email
              </DialogTitle>
            </DialogHeader>

            <DialogBody py="24px">
              <Flex direction="column" rowGap="24px">
                <Box fontSize="14px" fontWeight="400" color="neutral.400" lineHeight="150%">
                  Please verify your email to continue. Enter the 6-digit code sent to{' '}
                  <Box as="span" color="neutral.200">{user?.email}</Box>
                </Box>

                <Flex direction="column" rowGap="8px">
                  <Flex justifyContent="space-between">
                    <Box fontWeight="400" fontSize="14px" lineHeight="150%" color="neutral.300">
                      Verification Code
                    </Box>
                    {error && (
                      <Box textAlign="center" color="error.600" fontSize="14px" fontWeight="300">
                        {error}
                      </Box>
                    )}
                  </Flex>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    p="10px"
                    fontWeight="400"
                    fontSize="14px"
                    lineHeight="150%"
                    placeholder="Enter 6-digit code"
                    color="neutral.300"
                    borderWidth="1px"
                    borderColor="neutral.700"
                    backgroundColor="neutral.800"
                    borderRadius="8px"
                    autoFocus
                    value={code}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setCode(value);
                      setError(null);
                    }}
                    _focus={{
                      borderColor: 'neutral.600',
                      outline: 'none',
                    }}
                  />
                </Flex>

                <Button
                  height="fit-content"
                  width="100%"
                  py="8px"
                  fontWeight="500"
                  fontSize="14px"
                  color="neutral.900"
                  backgroundColor="neutral.100"
                  _hover={{
                    backgroundColor: 'neutral.200',
                  }}
                  loading={isLoading}
                  disabled={isLoading || code.length !== 6}
                  onClick={handleVerify}
                >
                  Verify Email
                </Button>

                <Flex justifyContent="center" alignItems="center" columnGap="4px">
                  <Box fontSize="14px" fontWeight="400" color="neutral.400">
                    Didn't receive the code?
                  </Box>
                  <Text
                    as="button"
                    fontSize="14px"
                    fontWeight="500"
                    color={resendCooldown > 0 ? 'neutral.500' : 'neutral.200'}
                    textDecoration="underline"
                    cursor={resendCooldown > 0 ? 'not-allowed' : 'pointer'}
                    bg="none"
                    border="none"
                    onClick={resendCooldown > 0 || isResending ? undefined : handleResendCode}
                    _hover={resendCooldown > 0 ? {} : { color: 'neutral.100' }}
                  >
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend'}
                  </Text>
                </Flex>
              </Flex>
            </DialogBody>
          </DialogContent>
        </DialogPositioner>
      </Portal>
    </DialogRoot>
  );
};

export default EmailVerificationModal;
